import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { compose, withStateHandlers, withHandlers } from "recompose";
import { useStore } from "react-redux";

import { Modal } from "components/molecules";
import GoogleMap from "components/core/GoogleMap";
import { SearchCardInfoWindow } from "components/search/SearchMap";
import SearchPin from "components/search/SearchMap/SearchPin";

import "./MapView.scss";

export const enhance = compose(
	withStateHandlers(
		() => ({
			facilitySelected: null,
			pinSelected: false
		}),
		{
			handleFacilitySelected: () => (facilityId) => ({
				facilitySelected: facilityId
			}),
			handleFacilityDeselected: () => () => ({
				facilitySelected: null
			}),
			handlePinSelected: () => () => ({
				pinSelected: true
			}),
			handlePinDeselected: () => () => ({
				pinSelected: false
			})
		}
	),
	withHandlers({
		handleSelect:
			({ handleFacilitySelected, handlePinSelected }) =>
			(facilityId) => {
				handleFacilitySelected(facilityId);
				handlePinSelected();
			},
		handleDeselect:
			({ handleFacilityDeselected, handlePinDeselected }) =>
			() => {
				handleFacilityDeselected();
				handlePinDeselected();
			}
	})
);

const withNormalizedLocation = (facility) => {
	if (!facility) return null;
	const location = facility.location || facility.schema?.location || null;
	if (!location) return null;

	return {
		...facility,
		id: facility.id ?? location.id,
		location,
		schema: {
			...(facility.schema || {}),
			location
		}
	};
};

export function MapView({
	isOpen,
	onClose,
	handleClose,
	facility,
	facilities,
	facilityCards,
	facilitySelected,
	handleSelect,
	handleDeselect,
	baseUrl
}) {
	const mapContainerRef = useRef(null);
	const mapInstanceRef = useRef(null);
	const googleInstanceRef = useRef(null);
	const hasFitBoundsRef = useRef(false);
	const reduxStore = useStore();
	const closeHandler = onClose || handleClose || (() => {});

	const normalizedFacilities = useMemo(() => {
		let facilitiesSource = [];

		if (Array.isArray(facilities) && facilities.length > 0) {
			facilitiesSource = facilities;
		} else if (Array.isArray(facilityCards?.facilities)) {
			facilitiesSource = facilityCards.facilities;
		}

		const currentFacility = withNormalizedLocation(facility);
		const listFacilities = facilitiesSource
			.map(withNormalizedLocation)
			.filter((facilityItem) => Boolean(facilityItem?.location));

		const mergedFacilities = currentFacility
			? [
					currentFacility,
					...listFacilities.filter(
						(facilityItem) => facilityItem.id !== currentFacility.id
					)
			  ]
			: listFacilities;

		return mergedFacilities.filter(
			(facilityItem) =>
				facilityItem?.location?.latitude != null &&
				facilityItem?.location?.longitude != null
		);
	}, [facility, facilities, facilityCards]);

	const normalizedFacility =
		withNormalizedLocation(facility) || normalizedFacilities[0] || null;

	const mapCenter = normalizedFacility?.location || null;

	const selectedFacility =
		normalizedFacilities.find(
			(f) => String(f.id) === String(facilitySelected)
		) || null;

	useEffect(() => {
		if (isOpen && normalizedFacility?.id != null) {
			handleSelect(normalizedFacility.id);
		}
	}, [isOpen, normalizedFacility?.id, handleSelect]);

	useEffect(() => {
		if (!isOpen) {
			hasFitBoundsRef.current = false;
			return;
		}

		if (hasFitBoundsRef.current) return;

		const map = mapInstanceRef.current;
		const google = googleInstanceRef.current;

		if (!map || !google || normalizedFacilities.length === 0) return;

		hasFitBoundsRef.current = true;

		const bounds = new google.maps.LatLngBounds();
		normalizedFacilities.forEach((facilityItem) => {
			const { latitude, longitude } = facilityItem.location || {};
			if (latitude != null && longitude != null) {
				bounds.extend(new google.maps.LatLng(latitude, longitude));
			}
		});

		if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
			const point = bounds.getNorthEast();
			bounds.extend(
				new google.maps.LatLng(
					point.lat() + 0.001,
					point.lng() + 0.001
				)
			);
			bounds.extend(
				new google.maps.LatLng(
					point.lat() - 0.001,
					point.lng() - 0.001
				)
			);
		}

		map.fitBounds(bounds, 0);
		const zoom = map.getZoom();
		if (typeof zoom === "number") {
			map.setZoom(Math.max(zoom - 1, 8));
		}
	}, [isOpen, normalizedFacilities]);

	useEffect(() => {
		const mapElement = mapContainerRef.current;

		if (mapElement && facilitySelected != null) {
			const handleMapClick = (event) => {
				if (!(event.target instanceof Element)) return;

				if (
					!event.target.closest(".gm-style-iw") &&
					!event.target.closest(".facility-marker-button") &&
					!event.target.closest(".search-facility-marker")
				) {
					handleDeselect();
				}
			};

			mapElement.addEventListener("click", handleMapClick);
			return () => {
				mapElement.removeEventListener("click", handleMapClick);
			};
		}

		return undefined;
	}, [facilitySelected, handleDeselect]);

	if (!mapCenter) return null;

	return (
		<Modal
			isOpen={isOpen}
			handleClose={closeHandler}
			size={Modal.SIZES.LARGE}
			closable
			toolbarContent="Map View"
			className="map-view-modal"
			ariaLabel="Facility map view"
		>
			{isOpen && (
				<div
					className="map-view-two"
					ref={mapContainerRef}
				>
					<GoogleMap
						center={mapCenter}
						radius={30}
						onMapReady={(mapInstance, googleInstance) => {
							mapInstanceRef.current = mapInstance;
							googleInstanceRef.current = googleInstance;
							hasFitBoundsRef.current = true;
						}}
					>
						{normalizedFacilities.map((facilityItem) => (
							<SearchPin
								key={facilityItem.id}
								facility={facilityItem}
								onClick={() => handleSelect(facilityItem.id)}
								selected={facilityItem.id === facilitySelected}
							/>
						))}
						{selectedFacility && (
							<SearchCardInfoWindow
								key={facilitySelected}
								facility={selectedFacility}
								facilitySelected={facilitySelected}
								handleFacilitySelected={handleSelect}
								handleFacilityDeselected={handleDeselect}
								handlePinDeselected={handleDeselect}
								onClose={handleDeselect}
								actions={{
									getFacilityCardPhoneNumber: () =>
										Promise.resolve({})
								}}
								location={selectedFacility.location}
								searchType="storage"
								handleFacilityCardVisible={() => {}}
								baseUrl={baseUrl}
								reduxStore={reduxStore}
								inMapView
							/>
						)}
					</GoogleMap>
				</div>
			)}
		</Modal>
	);
}

MapView.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	handleClose: PropTypes.func,
	facility: PropTypes.object,
	facilityCards: PropTypes.shape({
		facilities: PropTypes.arrayOf(PropTypes.object)
	}),
	facilities: PropTypes.arrayOf(PropTypes.object),
	facilitySelected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	handleSelect: PropTypes.func.isRequired,
	handleDeselect: PropTypes.func.isRequired,
	baseUrl: PropTypes.string
};

MapView.defaultProps = {
	isOpen: false,
	onClose: null,
	handleClose: null,
	facility: null,
	facilityCards: null,
	facilities: []
};

export default enhance(MapView);
